// @ts-ignore
/* eslint-disable */
import { ParamsType } from '@ant-design/pro-components';
import { request } from '@umijs/max';
import type { RequestOptionsInit } from '@umijs/max';
import { RestResponse } from 'mock/rest';

// Read about mocks here: https://v2-pro.ant.design/docs/mock-api

type RestResponseDto<T> = {
  data?: T;
  message?: string;
  status?: number;
};

// --- Auth APIs ---
/** POST /admin/auth/login */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  const payload = {
    identity: body.identity || body.username || '',
    password: body.password || '',
  };

  const response = await request<RestResponseDto<API.LoginResult>>('/api/admin/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: payload,
    ...(options || {}),
  });

  return response?.data || ({} as API.LoginResult);
}

/** Obtain current admin user profile. Will be called after login to store in initialState.
 *  GET /admin/auth/me */
export async function currentUser(options?: { [key: string]: any }) {
  return request<API.CurrentUser>('/api/admin/auth/me', {
    method: 'GET',
    ...(options || {}),
  });
}

/** POST /admin/auth/logout */
export async function logout(options?: { [key: string]: any }) {
  // Temporarily store the accessToken for the logout API call below
  const accessToken = localStorage.getItem('accessToken');
  // Clear stored token from local storage
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  await request<RestResponseDto<Record<string, any>>>('/api/admin/auth/logout', {
    method: 'POST',
    headers: {
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
    ...(options || {}),
  });

  return {};
}

// Helper function to handle token refresh
export async function refreshToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await request<{
      accessToken: string;
      refreshToken: string;
    }>('/api/admin/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: refreshToken ? `Bearer ${refreshToken}` : '',
      },
    });

    if (response.accessToken) {
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      console.log('Token renewed successfully');
      return response.accessToken;
    }
    return null;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return null;
  }
}

// Start calling refreshToken every 10 minutes.
// If there isn't valid token, refreshToken() does nothing.
window.setInterval(
  () => {
    console.log('Calling refresh token...');
    refreshToken();
  },
  10 * 60 * 1000,
);

// -- 2FA related --
/**
 * Generate QR code for 2FA setup (self-service for current admin)
 * GET /admin/auth/2fa/generate
 */
export async function generateQRcode2FA(options?: { [key: string]: any }): Promise<API.QrCode2FA> {
  const response = await request<RestResponse<API.QrCode2FA>>('/api/admin/auth/2fa/generate', {
    method: 'GET',
    ...(options || {}),
  });
  if (response.status == 200 && response.data) {
    return response.data;
  } else {
    return {
      qrCodeUrl: '',
      secret: '',
    };
  }
}

/**
 * Verify TOTP and activate 2FA (self-service for current admin)
 * POST /admin/auth/2fa/verify
 */
export async function verify2FA(
  data: { token: string },
  accessToken?: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponse<boolean>>('/api/admin/auth/2fa/verify', {
    method: 'POST',
    ...(accessToken && {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }),
    ...(options || {}),
    data: data,
  });
  if (response.status == 200 && response.data) {
    return response.data;
  } else {
    return false;
  }
}

/**
 * Unbind (disable) 2FA for any admin user (admin-managed)
 * DELETE /admin-users/{id}/2fa
 */
export async function unbind2FAForUser(
  id: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<boolean>>(`/api/admin-users/${id}/2fa`, {
    method: 'DELETE',
    ...(options || {}),
  });
  return Boolean(response?.data);
}

// -- Blocks ---
// Legacy block/post content endpoints below are not in the current swagger; UI routes are hidden until replacements exist.

/** Return all blocks
 * GET /blocks */
export async function queryBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/blocks', {
    method: 'GET',
    ...(options || {}),
  });
  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Get one block (all detail info) by id
 * GET /blocks/{id} */
export async function getBlock(
  id: string,
  options?: { [key: string]: any },
): Promise<API.Block | undefined> {
  const response = await request<RestResponse<API.Block>>(`/api/blocks/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
  if (response.status == 200) {
    return response.data;
  } else {
    return undefined;
  }
}

/** Saves one given block, if does not exist it will create new
 * POST /blocks/{id} */
export async function saveBlock(
  block: API.Block,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/blocks`, {
    method: 'POST',
    data: block,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

/** Delete given block by id
 * DELETE /blocks/{id} */
export async function deleteBlock(id: string, options?: { [key: string]: any }): Promise<boolean> {
  const response = await request<RestResponse<boolean>>(`/api/blocks/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

/** Return general blocks
 * GET /blocks */
export async function queryGeneralBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/general-blocks', {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return section-specific blocks
 * GET /blocks */
export async function querySectionBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/section-blocks', {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return ads blocks
 * GET /blocks */
export async function queryAdsBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/ads-blocks', {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return list of post IDs, used in select post drop-down
 * GET /post-list/:section */
export async function listPosts(
  section: string,
  options?: { [key: string]: any },
): Promise<{ id: string; title: string }[]> {
  const response = await request<RestResponse<{ id: string; title: string }[]>>(
    `/api/post-list2/${section}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return list of posts
 * GET /post/:section */
export async function queryPosts(
  section: string,
  params?: ParamsType & { pageSize?: number; current?: number },
  options?: { [key: string]: any },
): Promise<{ data: API.Post[]; metadata: { total: number } }> {
  const response = await request<RestResponse<API.Post[]>>(`/api/posts/${section}`, {
    method: 'GET',
    params: params,
    ...(options || {}),
  });

  if (response.status == 200) {
    return {
      data: response.data || [],
      metadata: {
        total: response.metadata?.total || 0,
      },
    };
  } else {
    return {
      data: [],
      metadata: {
        total: 0,
      },
    };
  }
}

/** Save one post
 * POST /post */
export async function savePost(
  section: string,
  data: API.Post,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/posts/${section}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

/** Delete one post
 * DELETE /post/:section/:id */
export async function deletePost(id: string, options?: { [key: string]: any }): Promise<boolean> {
  const response = await request<RestResponse<boolean>>(`/api/posts/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

// --- Site settings (swagger-aligned) ---
/** Return site settings
 * GET /site-settings */
export async function querySiteSetting(options?: { [key: string]: any }): Promise<API.SiteSetting> {
  const response = await request<RestResponse<API.SiteSetting>>(`/api/site-settings`, {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || ({} as API.SiteSetting);
  } else {
    return {} as API.SiteSetting;
  }
}

/** Save site settings
 * POST /site-settings */
export async function saveSiteSetting(
  data: API.SiteSetting,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/site-settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

// --- Recommendations ---
/** List recommendations by type
 * GET /recommend/{type} */
export async function queryRecommendations(
  type: API.RecommendationType,
  options?: { [key: string]: any },
): Promise<API.Recommendation[]> {
  const response = await request<RestResponse<API.Recommendation[]>>(
    `/api/recommend/${type}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Replace recommendations list
 * POST /recommend/{type} */
export async function replaceRecommendations(
  type: API.RecommendationType,
  data: API.Recommendation[],
  options?: { [key: string]: any },
): Promise<API.Recommendation[]> {
  const response = await request<RestResponse<API.Recommendation[]>>(`/api/recommend/${type}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Delete recommendation by id
 * DELETE /recommend/{type}/{id} */
export async function deleteRecommendation(
  type: API.RecommendationType,
  id: string,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/recommend/${type}/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

// --- Gallery detail ads ---
/** Return gallery detail ads payload
 * GET /gallery-detail-ads */
export async function queryGalleryDetailAds(
  options?: { [key: string]: any },
): Promise<API.GalleryDetailAd> {
  const response = await request<RestResponse<API.GalleryDetailAd>>(`/api/gallery-detail-ads`, {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || ({} as API.GalleryDetailAd);
  } else {
    return {} as API.GalleryDetailAd;
  }
}

/** Save gallery detail ads
 * POST /gallery-detail-ads */
export async function saveGalleryDetailAds(
  data: API.GalleryDetailAd,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/gallery-detail-ads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

// --- User Management ---
/** Query list of all users
 * GET /admin-users */
export async function queryUsers(options?: { [key: string]: any }): Promise<API.User[]> {
  const response = await request<RestResponseDto<API.AdminUser[]>>(`/api/admin-users`, {
    method: 'GET',
    ...(options || {}),
  });

  return Array.isArray(response?.data) ? response.data : [];
}

/** Create a new user
 * POST /admin-users */
export async function createUser(
  data: API.CreateAdminUserDto,
  options?: { [key: string]: any },
): Promise<API.AdminUser | undefined> {
  const response = await request<RestResponseDto<API.AdminUser>>(`/api/admin-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  return response?.data;
}

/** Delete a new user
 * DELETE /admin-users/:id */
export async function deleteUser(id: string, options?: { [key: string]: any }): Promise<boolean> {
  const response = await request<RestResponseDto<boolean>>(`/api/admin-users/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });

  return Boolean(response?.data);
}

/** Get one user by id
 * GET /admin-users/:id */
export async function getUserById(
  id: string,
  options?: { [key: string]: any },
): Promise<API.User | undefined> {
  const response = await request<RestResponseDto<API.AdminUser>>(`/api/admin-users/${id}`, {
    method: 'GET',
    ...(options || {}),
  });

  return response?.data;
}

/** Update one user by id
 * POST /admin-users/:id */
export async function updateUser(
  id: string,
  data: API.UpdateAdminUserDto,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<string>>(`/api/admin-users/${id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return typeof response?.data === 'string' && response.data.length > 0;
}

/** Update password only for given user
 * POST /admin-users/:id   */
export async function resetUserPassword(
  id: string,
  newPassword: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  return updateUser(
    id,
    {
      password: newPassword,
    },
    options,
  );
}

/** Update privileges only for given user
 * POST /admin-users/:id */
export async function updateUserPrivileges(
  id: string,
  privilege?: API.UserPrivilegeBitstring,
  options?: { [key: string]: any },
): Promise<boolean> {
  return updateUser(
    id,
    {
      privilege,
    },
    options,
  );
}

// --- Frontend User Management ---
/** Query frontend users
 * GET /users */
export async function queryFrontendUsers(
  params: API.FrontendUserListQuery,
  options?: { [key: string]: any },
): Promise<API.FrontendUserListResponseDto> {
  const response = await request<RestResponseDto<API.FrontendUserListResponseDto>>(`/api/users`, {
    method: 'GET',
    params,
    ...(options || {}),
  });

  return (
    response?.data || {
      items: [],
      total: 0,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    }
  );
}

/** Get frontend user detail
 * GET /users/{id} */
export async function getFrontendUser(
  id: number,
  options?: { [key: string]: any },
): Promise<API.FrontendUser | undefined> {
  const response = await request<RestResponseDto<API.FrontendUser>>(`/api/users/${id}`, {
    method: 'GET',
    ...(options || {}),
  });

  return response?.data;
}

/** Create frontend user
 * POST /users */
export async function createFrontendUser(
  data: API.CreateFrontendUserDto,
  options?: { [key: string]: any },
): Promise<API.FrontendUser | undefined> {
  const response = await request<RestResponseDto<API.FrontendUser>>(`/api/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return response?.data;
}

/** Update frontend user
 * PATCH /users/{id} */
export async function updateFrontendUser(
  id: number,
  data: API.UpdateFrontendUserDto,
  options?: { [key: string]: any },
): Promise<API.FrontendUser | undefined> {
  const response = await request<RestResponseDto<API.FrontendUser>>(`/api/users/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return response?.data;
}

/** Delete frontend user
 * DELETE /users/{id} */
export async function deleteFrontendUser(
  id: number,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<boolean>>(`/api/users/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  return Boolean(response?.data);
}

/** Reset frontend user password
 * POST /users/{id}/reset-password */
export async function resetFrontendUserPassword(
  id: number,
  options?: { [key: string]: any },
): Promise<API.ResetFrontendUserPasswordResponseDto | undefined> {
  const response = await request<RestResponseDto<API.ResetFrontendUserPasswordResponseDto>>(
    `/api/users/${id}/reset-password`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );

  return response?.data;
}

/** Update frontend user account status
 * PATCH /users/{id}/status */
export async function updateFrontendUserStatus(
  id: number,
  data: API.UpdateFrontendUserStatusDto,
  options?: { [key: string]: any },
): Promise<API.FrontendUser | undefined> {
  const response = await request<RestResponseDto<API.FrontendUser>>(`/api/users/${id}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return response?.data;
}

/** Update frontend user posting permission
 * PATCH /users/{id}/posting-permission */
export async function updateFrontendUserPostingPermission(
  id: number,
  data: API.ToggleFrontendUserPermissionDto,
  options?: { [key: string]: any },
): Promise<API.FrontendUser | undefined> {
  const response = await request<RestResponseDto<API.FrontendUser>>(
    `/api/users/${id}/posting-permission`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );

  return response?.data;
}

/** Update frontend user commenting permission
 * PATCH /users/{id}/commenting-permission */
export async function updateFrontendUserCommentingPermission(
  id: number,
  data: API.ToggleFrontendUserPermissionDto,
  options?: { [key: string]: any },
): Promise<API.FrontendUser | undefined> {
  const response = await request<RestResponseDto<API.FrontendUser>>(
    `/api/users/${id}/commenting-permission`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );

  return response?.data;
}

/** Batch update frontend user status
 * POST /users/batch/status */
export async function batchUpdateFrontendUserStatus(
  data: API.BatchFrontendUserStatusDto,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<boolean>>(`/api/users/batch/status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return Boolean(response?.data);
}

/** Batch update frontend user posting permission
 * POST /users/batch/posting-permission */
export async function batchUpdateFrontendUserPostingPermission(
  data: API.BatchToggleFrontendUserPermissionDto,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<boolean>>(
    `/api/users/batch/posting-permission`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );

  return Boolean(response?.data);
}

/** Batch update frontend user commenting permission
 * POST /users/batch/commenting-permission */
export async function batchUpdateFrontendUserCommentingPermission(
  data: API.BatchToggleFrontendUserPermissionDto,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<boolean>>(
    `/api/users/batch/commenting-permission`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );

  return Boolean(response?.data);
}

// --- Admin Posts ---
/** Query admin posts
 * GET /posts */
export async function queryAdminPosts(
  params: API.AdminPostListQuery,
  options?: { [key: string]: any },
): Promise<API.AdminPostListResponseDto> {
  const response = await request<RestResponseDto<API.AdminPostListResponseDto>>(`/api/posts`, {
    method: 'GET',
    params,
    ...(options || {}),
  });

  return (
    response?.data || {
      items: [],
      total: 0,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    }
  );
}

/** Get admin post detail
 * GET /posts/{id} */
export async function getAdminPost(
  id: number,
  options?: { [key: string]: any },
): Promise<API.AdminPostDto | undefined> {
  const response = await request<RestResponseDto<API.AdminPostDto>>(`/api/posts/${id}`, {
    method: 'GET',
    ...(options || {}),
  });

  return response?.data;
}

/** Update admin post
 * PATCH /posts/{id} */
export async function updateAdminPost(
  id: number,
  data: API.AdminUpdatePostDto,
  options?: { [key: string]: any },
): Promise<API.AdminPostDto | undefined> {
  const response = await request<RestResponseDto<API.AdminPostDto>>(`/api/posts/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return response?.data;
}

/** Delete admin post
 * DELETE /posts/{id} */
export async function deleteAdminPost(
  id: number,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<string>>(`/api/posts/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  return Boolean(response?.data);
}

/** Toggle admin post approval
 * POST /posts/{id}/approve */
export async function toggleAdminPostApproval(
  id: number,
  data?: API.PostApprovalToggleDto,
  options?: { [key: string]: any },
): Promise<API.PostApprovalResultDto | undefined> {
  const requestConfig: RequestOptionsInit = {
    method: 'POST',
    ...(options || {}),
  };

  if (data) {
    requestConfig.headers = {
      'Content-Type': 'application/json',
    };
    requestConfig.data = data;
  }

  const response = await request<RestResponseDto<API.PostApprovalResultDto>>(
    `/api/posts/${id}/approve`,
    requestConfig,
  );

  return response?.data;
}

/** Batch toggle admin post approval
 * POST /posts/approve */
export async function batchToggleAdminPostApproval(
  data: API.PostApprovalBatchDto,
  options?: { [key: string]: any },
): Promise<API.PostApprovalBatchResultDto | undefined> {
  const response = await request<RestResponseDto<API.PostApprovalBatchResultDto>>(
    `/api/posts/approve`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );

  return response?.data;
}

// --- Admin Comments ---
/** Query admin comments
 * GET /comments */
export async function queryAdminComments(
  params: API.AdminCommentListQuery,
  options?: { [key: string]: any },
): Promise<API.AdminCommentListResponseDto> {
  const response = await request<RestResponseDto<API.AdminCommentListResponseDto>>(
    `/api/comments`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );

  return (
    response?.data || {
      items: [],
      total: 0,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    }
  );
}

/** Get admin comment detail
 * GET /comments/{id} */
export async function getAdminComment(
  id: number,
  options?: { [key: string]: any },
): Promise<API.AdminCommentDto | undefined> {
  const response = await request<RestResponseDto<API.AdminCommentDto>>(`/api/comments/${id}`, {
    method: 'GET',
    ...(options || {}),
  });

  return response?.data;
}

/** Update admin comment
 * PATCH /comments/{id} */
export async function updateAdminComment(
  id: number,
  data: API.UpdateCommentDto,
  options?: { [key: string]: any },
): Promise<API.AdminCommentDto | undefined> {
  const response = await request<RestResponseDto<API.AdminCommentDto>>(`/api/comments/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });

  return response?.data;
}

/** Delete admin comment
 * DELETE /comments/{id} */
export async function deleteAdminComment(
  id: number,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponseDto<string>>(`/api/comments/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  return Boolean(response?.data);
}

/** Toggle admin comment approval
 * POST /comments/{id}/approve */
export async function toggleAdminCommentApproval(
  id: number,
  data?: API.CommentApprovalToggleDto,
  options?: { [key: string]: any },
): Promise<API.CommentApprovalResultDto | undefined> {
  const requestConfig: RequestOptionsInit = {
    method: 'POST',
    ...(options || {}),
  };

  if (data) {
    requestConfig.headers = {
      'Content-Type': 'application/json',
    };
    requestConfig.data = data;
  }

  const response = await request<RestResponseDto<API.CommentApprovalResultDto>>(
    `/api/comments/${id}/approve`,
    requestConfig,
  );

  return response?.data;
}

/** Batch toggle admin comment approval
 * POST /comments/approve */
export async function batchToggleAdminCommentApproval(
  data: API.CommentApprovalBatchDto,
  options?: { [key: string]: any },
): Promise<API.CommentApprovalBatchResultDto | undefined> {
  const response = await request<RestResponseDto<API.CommentApprovalBatchResultDto>>(
    `/api/comments/approve`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );

  return response?.data;
}

/** Change own password
 * POST /change-password */
// --- Lottery Draw Records ---
/** List lottery draw records with pagination
 * GET /lottery-data/lottery-draw-records */
export async function getLotteryDrawRecords(
  params: API.LotteryDrawRecordsQuery,
  options?: { [key: string]: any },
): Promise<API.AdminLotteryDrawRecordsListResponseDto> {
  const response = await request<RestResponseDto<API.AdminLotteryDrawRecordsListResponseDto>>(
    `/api/lottery-data/lottery-draw-records`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );

  return (
    response?.data || {
      items: [],
      total: 0,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    }
  );
}

/** Delete a lottery draw record by lottery_type and issue
 * DELETE /lottery-data/lottery-draw-records */
export async function deleteLotteryDrawRecord(
  params: { lottery_type: number; issue: number },
  options?: { [key: string]: any },
): Promise<boolean> {
  try {
    await request(`/api/lottery-data/lottery-draw-records`, {
      method: 'DELETE',
      params,
      ...(options || {}),
    });
    return true;
  } catch (error) {
    console.error('Failed to delete lottery draw record:', error);
    return false;
  }
}

/** Submit a new lottery draw record
 * POST /lottery-data/lottery-draw-records */
export async function createLotteryDrawRecord(
  data: API.CreateLotteryDrawRecordDto,
  options?: { [key: string]: any },
): Promise<API.LotteryDrawRecordDto | undefined> {
  const response = await request<RestResponseDto<API.LotteryDrawRecordDto>>(
    `/api/lottery-data/lottery-draw-records`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      skipErrorHandler: true,
      ...(options || {}),
    },
  );

  return response?.data;
}

export async function changePassword(
  newPassword: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  try {
    await request(`/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        password: newPassword,
      },
      ...(options || {}),
    });

    // If we get here, the request was successful (no error thrown)
    return true;
  } catch (error) {
    console.error('Failed to change password:', error);
    return false;
  }
}
